import { FieldValues } from 'react-hook-form';
import { TableCell, DateCell, StatusCell } from 'ui/tableCell';
import { ProjectsActionButton } from './components/ProjectsActionButton';
import { uuidProjectTypeByUniqueIdentifier } from './constants';
import {
  UniqueIdentifierValues,
  UserIdConfig,
  CreateProjectPayload,
  ProjectsTableColumnData,
  Project,
  ConfigureProjectColumnsProps,
} from './types';

export const createUserIdConfig = (uniqueIdentifier: UniqueIdentifierValues) => {
  const isUuidBased = uniqueIdentifier !== UniqueIdentifierValues.email;
  const payload = { isUuidBased } as UserIdConfig;
  if (isUuidBased) payload.uuidProjectType = uuidProjectTypeByUniqueIdentifier[uniqueIdentifier];
  return payload;
};

export const createProjectPayload = ({
  name,
  timeZone,
  uniqueIdentifier,
  shardCount,
}: FieldValues): CreateProjectPayload => {
  const payload = {
    name,
    timeZone,
    shardCount,
  } as CreateProjectPayload;
  const userIdConfig = createUserIdConfig(uniqueIdentifier);
  return { ...payload, ...userIdConfig };
};

export const configureColumns = ({
  organizations,
  openArchiveModal,
  openEditModal,
  openUnarchiveModal,
}: ConfigureProjectColumnsProps): ProjectsTableColumnData[] => {
  return [
    {
      name: 'ID',
      align: 'flex-start',
      value: 'id',
      RowCell: ({ row }) => <TableCell>{row.id}</TableCell>,
    },
    {
      name: 'Org ID',
      align: 'flex-start',
      value: 'organizationId',
      RowCell: ({ row }) => <TableCell>{row.organizationId}</TableCell>,
    },
    {
      name: 'Project name',
      align: 'flex-start',
      value: 'name',
      RowCell: ({ row }) => <TableCell>{row.name}</TableCell>,
    },
    {
      name: 'Created',
      align: 'flex-start',
      value: 'createdAt',
      RowCell: ({ row }) => <DateCell timestamp={row.createdAt} />,
    },
    {
      name: 'Creator',
      align: 'flex-start',
      value: 'creatorUserId',
      RowCell: ({ row }) => <TableCell>{row.creatorUserId}</TableCell>,
    },
    {
      name: 'Project type',
      align: 'flex-start',
      value: 'isSandbox',
      sortable: false,
      RowCell: ({ row }) => <TableCell>{row.isSandbox ? 'Sandbox' : 'Normal'}</TableCell>,
    },
    {
      name: 'Status',
      align: 'flex-start',
      value: 'isEnabled',
      sortable: false,
      RowCell: ({ row }) => {
        const organization = organizations.find((org) => org.id === row.organizationId);
        const negativeLabel =
          organization?.isEnabled === false ? 'Org disabled' : 'Project archived';
        return (
          <StatusCell
            data-test="project-status-cell"
            positiveLabel="Enabled"
            showPositiveState={!!organization?.isEnabled}
            negativeLabel={negativeLabel}
          />
        );
      },
    },
    {
      name: '',
      align: 'flex-end',
      value: '',
      sortable: false,
      RowCell: ({ row }) => (
        <ProjectsActionButton
          openArchiveModal={openArchiveModal}
          openEditModal={openEditModal}
          openUnarchiveModal={openUnarchiveModal}
          project={row}
        />
      ),
    },
  ];
};

export const projectTableFilterFn = (project: Project, filter: string) => {
  if (project.id.toString().includes(filter)) return true;
  if (project.organizationId.toString().includes(filter)) return true;
  if (project.name.toLowerCase().includes(filter.toLowerCase())) return true;
  if (project.creatorUserId.toLowerCase().includes(filter.toLowerCase())) return true;

  return false;
};
